import {of, from} from 'rxjs';
import i18next from 'i18next';
import {flatMap, catchError} from 'rxjs/operators';
import {reset, change} from 'redux-form';

import {combineEpics} from 'web-app/util/redux-observable';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {createEmployeeObservable} from 'web-app/react/entities/employees/epics';
import * as NewEmployeeModalActions from 'web-app/react/modals-container/new-employee-modal/actions';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import LoginsApi from 'web-app/api/logins-api';
import * as EmployeeActions from 'web-app/react/entities/employees/actions';

import * as Actions from './actions';
import * as Constants from './constants';
import {fieldNames} from './new-employee-form/types';

const createEmployee = action$ =>
    action$
        .ofType(Actions.createNewEmployee.type)
        .mergeMap(({payload}: ReturnType<typeof Actions.createNewEmployee.action>) => {
            const {institutionId, formData} = payload;

            return createEmployeeObservable(institutionId, formData.name, formData.email, formData.groupId).pipe(
                flatMap((action: any) => {
                    const successActions = [
                        action,
                        Actions.createNewEmployeeSuccess.action(),
                        // If the user selected 'Add another' we should re-open a new modal and reset the form data to
                        // create a new employee once the first one has been created.
                        // But we'll want to keep the flag that the user selected 'Add another' ticked,
                        // so users can create multiple employees in a row.
                        formData.addAnother
                            ? [
                                  reset(Constants.FORM_NAME),
                                  change(Constants.FORM_NAME, fieldNames('addAnother'), true),
                                  NewEmployeeModalActions.show.action(
                                      institutionId,
                                      formData.groupId,
                                      formData.addAnother,
                                  ),
                              ]
                            : null,
                        GlobalEventActions.updateSuccess.action(
                            i18next.t('employeeProfile.created', {name: formData.name}),
                        ),
                        // Fetch the new employee and put it in the entities store
                        EmployeeActions.fetchEmployee.action(action.payload.employee.employeeId),
                    ]
                        .filter(hasValue)
                        .flatMap(a => a);
                    if (formData.employeeRoleId !== Constants.DONT_SEND_LOGIN_KEY && formData.email) {
                        return from(
                            LoginsApi.assignRole(
                                formData.employeeRoleId,
                                action.payload.employee.famlyId,
                                institutionId,
                            ),
                        ).pipe(flatMap(() => successActions));
                    }
                    return successActions;
                }),
                catchError(e => of(Actions.createNewEmployeeFailed.action(e))),
            );
        });

export default combineEpics(createEmployee);
